/**
 * Task Details Modal
 * Shows complete task information, QR code, status tracking, reviews, and actions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import tasksService from '../../services/tasks';
import reviewsService from '../../services/reviews';
import { ReviewForm } from '../Review';
import './TaskDetailsModal.css';

const TaskDetailsModal = ({ taskId, onClose, onUpdate }) => {
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [existingReview, setExistingReview] = useState(null);

  const fetchTaskDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setExistingReview(null);

      // Fetch task details
      const taskData = await tasksService.getTaskById(taskId);
      setTask(taskData);

      // Generate QR code
      try {
        const qrData = await tasksService.generateQRCode(taskId);
        setQrCodeUrl(qrData.qrImageUrl);
      } catch (qrError) {
        console.error('QR code generation failed:', qrError);
        // Don't fail the whole modal if QR generation fails
      }

      // Fetch existing review for delivered jobs
      if (taskData.status === 'Delivered') {
        try {
          const review = await reviewsService.getReviewByJob(taskId);
          setExistingReview(review);
        } catch (reviewError) {
          console.error('Failed to fetch review:', reviewError);
          // Don't fail if review fetch fails
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load task details');
      console.error('Task details error:', err);
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    if (taskId) {
      fetchTaskDetails();
    }
  }, [taskId, fetchTaskDetails]);

  const getStatusColor = (status) => {
    const colors = {
      'Pending': '#FFA500',
      'Approved': '#17A2B8',
      'Assigned': '#3498DB',
      'Picked Up': '#8E44AD',
      'In Transit': '#9B59B6',
      'Delivered': '#27AE60',
      'Exception': '#E74C3C',
      'Returned': '#95A5A6',
      'Cancelled': '#6C757D'
    };
    return colors[status] || '#666';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'Pending': '⏳',
      'Approved': '✓',
      'Assigned': '👤',
      'Picked Up': '📦',
      'In Transit': '🚚',
      'Delivered': '✅',
      'Exception': '⚠️',
      'Returned': '↩️',
      'Cancelled': '❌'
    };
    return icons[status] || '📦';
  };

  const handleCopyTrackingLink = () => {
    if (task?.jobId) {
      const trackingUrl = `${window.location.origin}/track/${task.jobId}`;
      navigator.clipboard.writeText(trackingUrl);
      alert('Tracking link copied to clipboard!');
    }
  };

  const handleDownloadQR = () => {
    if (qrCodeUrl) {
      const link = document.createElement('a');
      link.href = qrCodeUrl;
      link.download = `QR-${task.jobId}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePrintQR = () => {
    window.print();
  };

  const handleCancelJob = async () => {
    if (!window.confirm('Are you sure you want to cancel this order? This action cannot be undone.')) {
      return;
    }

    try {
      setActionLoading(true);
      // Use the dedicated cancel endpoint
      await tasksService.cancelTask(taskId, 'Cancelled by client');

      alert('Order cancelled successfully');
      if (onUpdate) onUpdate();
      onClose();
    } catch (err) {
      alert(err.message || 'Failed to cancel order');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditJob = () => {
    // Check if job can be edited - locked once picked up by driver
    const lockedStatuses = ['Picked Up', 'In Transit', 'Delivered', 'Exception', 'Returned', 'Cancelled'];
    if (lockedStatuses.includes(task.status)) {
      alert(`Cannot edit order. Order is ${task.status.toLowerCase()} and locked for editing.`);
      return;
    }

    // Navigate to edit page
    navigate(`/edit-task/${taskId}`);
  };

  const canCancelOrEdit = () => {
    if (!task) return false;
    // Can only cancel/edit before driver picks up the package
    const lockedStatuses = ['Picked Up', 'In Transit', 'Delivered', 'Exception', 'Returned', 'Cancelled'];
    return !lockedStatuses.includes(task.status);
  };

  const handleReviewSubmitted = (review) => {
    setExistingReview(review);
  };

  const canShowReviewSection = () => {
    // Show review section only for delivered jobs with an assigned driver
    return task && task.status === 'Delivered' && task.driver;
  };

  if (!taskId) return null;

  return (
    <div className="task-details-overlay" onClick={onClose}>
      <div className="task-details-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="task-details-close" onClick={onClose}>
          ✕
        </button>

        {loading ? (
          <div className="task-details-loading">
            <div className="spinner"></div>
            <p>Loading task details...</p>
          </div>
        ) : error ? (
          <div className="task-details-error">
            <p>{error}</p>
            <button onClick={onClose} className="btn-secondary">Close</button>
          </div>
        ) : task ? (
          <div className="task-details-content">
            {/* Header */}
            <div className="task-details-header">
              <div className="task-details-header-left">
                <span className="task-details-icon" style={{ fontSize: '48px' }}>
                  {getStatusIcon(task.status)}
                </span>
                <div>
                  <h2 className="task-details-title">Order {task.jobId}</h2>
                  <div
                    className="task-details-status-badge"
                    style={{ backgroundColor: getStatusColor(task.status) }}
                  >
                    {task.status}
                  </div>
                </div>
              </div>
              <div className="task-details-priority">
                Priority: <strong>{task.priority}</strong>
              </div>
            </div>

            {/* Action Buttons */}
            {canCancelOrEdit() && (
              <div className="task-details-actions">
                <button
                  onClick={handleEditJob}
                  className="task-details-btn task-details-btn-edit"
                  disabled={actionLoading}
                >
                  ✏️ Edit Order
                </button>
                <button
                  onClick={handleCancelJob}
                  className="task-details-btn task-details-btn-cancel"
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Cancelling...' : '🗑️ Cancel Order'}
                </button>
              </div>
            )}

            {['Picked Up', 'In Transit', 'Delivered', 'Exception', 'Returned', 'Cancelled'].includes(task.status) ? (
              <div className="task-details-locked-message">
                🔒 This order is locked and cannot be edited because it is {task.status.toLowerCase()}.
              </div>
            ) : null}

            {/* Main Content Grid */}
            <div className="task-details-grid">
              {/* Left Column - Route & Details */}
              <div className="task-details-left">
                {/* Route Information */}
                <div className="task-details-section">
                  <h3 className="task-details-section-title">Delivery Route</h3>
                  <div className="task-details-route">
                    <div className="task-details-location">
                      <div className="task-details-location-icon pickup">📍</div>
                      <div>
                        <p className="task-details-location-label">Pickup</p>
                        <p className="task-details-location-name">{task.from?.name}</p>
                        <p className="task-details-location-address">{task.from?.address}</p>
                        {task.from?.phone && (
                          <a href={`tel:${task.from.phone}`} className="task-details-location-phone-link">
                            <span className="phone-icon">📞</span>
                            {task.from.phone}
                          </a>
                        )}
                      </div>
                    </div>

                    <div className="task-details-route-line"></div>

                    <div className="task-details-location">
                      <div className="task-details-location-icon delivery">🎯</div>
                      <div>
                        <p className="task-details-location-label">Delivery</p>
                        <p className="task-details-location-name">{task.to?.name}</p>
                        <p className="task-details-location-address">{task.to?.address}</p>
                        {task.to?.phone && (
                          <a href={`tel:${task.to.phone}`} className="task-details-location-phone-link">
                            <span className="phone-icon">📞</span>
                            {task.to.phone}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Schedule - Pickup & Delivery */}
                {(task.pickupDate || task.pickupWindow?.start || task.deliveryDate || task.deliveryWindow?.start || task.estimatedArrival) && (
                  <div className="task-details-section">
                    <h3 className="task-details-section-title">Schedule</h3>
                    <div className="task-details-schedule-grid">
                      {/* Pickup Schedule */}
                      <div className="task-details-schedule-card pickup">
                        <div className="task-details-schedule-header">
                          <span className="task-details-schedule-icon">📦</span>
                          <span className="task-details-schedule-title">Pickup</span>
                        </div>
                        <div className="task-details-schedule-content">
                          {task.pickupDate ? (
                            <div className="task-details-schedule-row">
                              <span className="task-details-schedule-label">Date:</span>
                              <span className="task-details-schedule-value">
                                {new Date(task.pickupDate).toLocaleDateString('en-US', {
                                  weekday: 'short', month: 'short', day: 'numeric'
                                })}
                              </span>
                            </div>
                          ) : (
                            <div className="task-details-schedule-row">
                              <span className="task-details-schedule-label">Date:</span>
                              <span className="task-details-schedule-value na">Not scheduled</span>
                            </div>
                          )}
                          {(task.pickupWindow?.start || task.pickupWindow?.end) ? (
                            <div className="task-details-schedule-row">
                              <span className="task-details-schedule-label">Time Window:</span>
                              <span className="task-details-schedule-value">
                                {task.pickupWindow.start || '??:??'} - {task.pickupWindow.end || '??:??'}
                              </span>
                            </div>
                          ) : (
                            <div className="task-details-schedule-row">
                              <span className="task-details-schedule-label">Time Window:</span>
                              <span className="task-details-schedule-value na">N/A</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Delivery Schedule */}
                      <div className="task-details-schedule-card delivery">
                        <div className="task-details-schedule-header">
                          <span className="task-details-schedule-icon">🎯</span>
                          <span className="task-details-schedule-title">Delivery</span>
                        </div>
                        <div className="task-details-schedule-content">
                          {task.deliveryDate ? (
                            <div className="task-details-schedule-row">
                              <span className="task-details-schedule-label">Date:</span>
                              <span className="task-details-schedule-value">
                                {new Date(task.deliveryDate).toLocaleDateString('en-US', {
                                  weekday: 'short', month: 'short', day: 'numeric'
                                })}
                              </span>
                            </div>
                          ) : (
                            <div className="task-details-schedule-row">
                              <span className="task-details-schedule-label">Date:</span>
                              <span className="task-details-schedule-value na">Not scheduled</span>
                            </div>
                          )}
                          {(task.deliveryWindow?.start || task.deliveryWindow?.end) ? (
                            <div className="task-details-schedule-row">
                              <span className="task-details-schedule-label">Time Window:</span>
                              <span className="task-details-schedule-value">
                                {task.deliveryWindow.start || '??:??'} - {task.deliveryWindow.end || '??:??'}
                              </span>
                            </div>
                          ) : (
                            <div className="task-details-schedule-row">
                              <span className="task-details-schedule-label">Time Window:</span>
                              <span className="task-details-schedule-value na">N/A</span>
                            </div>
                          )}
                          {task.estimatedArrival && (
                            <div className="task-details-schedule-row">
                              <span className="task-details-schedule-label">ETA:</span>
                              <span className="task-details-schedule-value highlight">
                                {new Date(task.estimatedArrival).toLocaleString('en-US', {
                                  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                                })}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Driver Info */}
                {task.driver && (
                  <div className="task-details-section">
                    <h3 className="task-details-section-title">Assigned Driver</h3>
                    <div className="task-details-driver">
                      <div className="task-details-driver-avatar">
                        {task.driver.firstName?.charAt(0)}{task.driver.lastName?.charAt(0)}
                      </div>
                      <div>
                        <p className="task-details-driver-name">
                          {task.driver.firstName} {task.driver.lastName}
                        </p>
                        {task.driver.email && (
                          <p className="task-details-driver-contact">{task.driver.email}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Review Section - Only for delivered jobs */}
                {canShowReviewSection() && (
                  <div className="task-details-section task-details-review-section">
                    <h3 className="task-details-section-title">
                      {existingReview ? 'Your Review' : 'Leave a Review'}
                    </h3>
                    <ReviewForm
                      job={{ _id: taskId, id: taskId }}
                      driver={task.driver}
                      existingReview={existingReview}
                      onReviewSubmitted={handleReviewSubmitted}
                    />
                  </div>
                )}

                {/* Additional Details */}
                <div className="task-details-section">
                  <h3 className="task-details-section-title">Additional Information</h3>
                  <div className="task-details-info-grid">
                    <div className="task-details-info-item">
                      <span className="task-details-info-label">Created:</span>
                      <span className="task-details-info-value">
                        {new Date(task.createdAt).toLocaleString()}
                      </span>
                    </div>
                    <div className="task-details-info-item">
                      <span className="task-details-info-label">Scheduled:</span>
                      <span className="task-details-info-value">
                        {new Date(task.scheduledDate).toLocaleString()}
                      </span>
                    </div>
                    {task.deliveredAt && (
                      <div className="task-details-info-item">
                        <span className="task-details-info-label">Delivered:</span>
                        <span className="task-details-info-value">
                          {new Date(task.deliveredAt).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                  {task.notes && (
                    <div className="task-details-notes">
                      <p className="task-details-info-label">Notes:</p>
                      <p className="task-details-notes-text">{task.notes}</p>
                    </div>
                  )}
                </div>

                {/* Status Timeline */}
                <div className="task-details-section">
                  <h3 className="task-details-section-title">Status History</h3>
                  <div className="task-details-timeline">
                    {task.statusHistory && task.statusHistory.length > 0 ? (
                      task.statusHistory
                        .slice()
                        .reverse()
                        .map((history, index) => (
                          <div key={index} className="task-details-timeline-item">
                            <div
                              className="task-details-timeline-marker"
                              style={{ backgroundColor: getStatusColor(history.status) }}
                            >
                              <span>{getStatusIcon(history.status)}</span>
                            </div>
                            <div className="task-details-timeline-content">
                              <p className="task-details-timeline-status">{history.status}</p>
                              <p className="task-details-timeline-time">
                                {new Date(history.timestamp).toLocaleString()}
                              </p>
                              {history.notes && (
                                <p className="task-details-timeline-notes">{history.notes}</p>
                              )}
                            </div>
                          </div>
                        ))
                    ) : (
                      <p className="task-details-no-history">No status updates available</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column - QR Code & Actions */}
              <div className="task-details-right">
                {/* QR Code Section */}
                <div className="task-details-section task-details-qr-section">
                  <h3 className="task-details-section-title">QR Code</h3>
                  <div className="task-details-qr-container">
                    {qrCodeUrl ? (
                      <>
                        <img
                          src={qrCodeUrl}
                          alt="QR Code"
                          className="task-details-qr-image"
                        />
                        <p className="task-details-qr-code-text">{task.qrCode}</p>
                      </>
                    ) : (
                      <div className="task-details-qr-loading">
                        <p>QR Code not available</p>
                      </div>
                    )}
                  </div>

                  {/* QR Actions */}
                  {qrCodeUrl && (
                    <div className="task-details-qr-actions">
                      <button
                        onClick={handleDownloadQR}
                        className="task-details-btn task-details-btn-secondary"
                      >
                        📥 Download
                      </button>
                      <button
                        onClick={handlePrintQR}
                        className="task-details-btn task-details-btn-secondary"
                      >
                        🖨️ Print
                      </button>
                    </div>
                  )}
                </div>

                {/* Public Tracking Link */}
                <div className="task-details-section">
                  <h3 className="task-details-section-title">Public Tracking</h3>
                  <div className="task-details-tracking-link">
                    <input
                      type="text"
                      value={`${window.location.origin}/track/${task.jobId}`}
                      readOnly
                      className="task-details-tracking-input"
                      onClick={(e) => e.target.select()}
                    />
                    <button
                      onClick={handleCopyTrackingLink}
                      className="task-details-btn task-details-btn-primary"
                    >
                      Copy Link
                    </button>
                  </div>
                  <p className="task-details-tracking-hint">
                    Share this link with customers to track their order without login
                  </p>
                </div>

                {/* Quick Stats */}
                <div className="task-details-section">
                  <h3 className="task-details-section-title">Quick Stats</h3>
                  <div className="task-details-stats">
                    <div className="task-details-stat-item">
                      <span className="task-details-stat-label">Job ID</span>
                      <span className="task-details-stat-value">{task.jobId}</span>
                    </div>
                    <div className="task-details-stat-item">
                      <span className="task-details-stat-label">Priority</span>
                      <span className="task-details-stat-value">{task.priority}</span>
                    </div>
                    <div className="task-details-stat-item">
                      <span className="task-details-stat-label">Status</span>
                      <span className="task-details-stat-value">{task.status}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default TaskDetailsModal;
